import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import type { LocalizedText } from '../../../lib/localizedText'
import { InputSwitch } from 'primereact/inputswitch'
import Badge from '../../../components/ui/Badge'
import Button from '../../../components/ui/Button'
import type { NotificationTemplate } from '../types'

interface ColumnOpts {
  t: TFunction
  /** İki dilli alanlar tek kolondur; değer seçili dilden gelir (`lib/localizedText`). */
  localized: LocalizedText
  onEdit: (row: NotificationTemplate) => void
  onToggle: (row: NotificationTemplate, active: boolean) => void
  onDelete: (row: NotificationTemplate) => void
  /** Durum/silme isteği sürerken satır kontrolleri kilitlenir (çift gönderim engeli). */
  busyId?: number | null
  /** Tekil kayıt çekilirken (form açılışı) düzenle butonları kilitlenir — çift tık engeli. */
  busy?: boolean
  /**
   * Salt okunur kip (126 — Kısıtlı Yetkili asistan): durum anahtarı görünür ama pasiftir (durum
   * okunabilsin), Sil çizilmez ve kalem yerine <b>Görüntüle</b> (göz) gelir. Şablonun konu/içerik/alıcı
   * metinleri yalnız pop-up'ta göründüğü için `onEdit` aynı tekil yüklemeyle pop-up'ı salt okunur açar.
   */
  readOnly?: boolean
}

/**
 * Bildirim Şablonu tablosu sütunları (059 US1).
 *
 * **Sıralama sunucu-taraflıdır** (`useTableSort`): veri sütunlarının tamamı `sortable`, aksiyon sütunu
 * değildir. Alan adları sunucudaki beyaz listeyle birebir aynı olmalıdır (`operationCodeName`,
 * `notificationTypeName`, `subjectTr`, `mailTo`, `recordStatus`); İşlem Kodu ve Bildirim Türü
 * sütunlarında sunucu **gösterilen dile göre çözülmüş adı** sıralar, kimliği değil. Durum sütunu
 * etiket gösterir ama sayısal `recordStatus` üzerinden sıralanır.
 *
 * Bilinçli olarak **yok olan**: **içerik sütunu** (CLOB; liste yanıtı bu alanları taşımaz — research
 * D10) → sıralanabilir bir içerik sütunu da yoktur.
 *
 * Boş değerler (`mailTo`, EN alanlar) hücrede **boş** bırakılır; `-`/`—` yazılmaz (anayasa: boş hücre
 * gösterimi). E-posta dışı türlerde `mailTo` her zaman boştur — bu **beklenen** durumdur.
 *
 * Durum satır-içi **toggle** ile değiştirilir (anayasa: lookup ekranı kalıbı — formda durum alanı
 * yoktur) ve silme **onay adımı** içerir; onay akışı çağıran sayfadadır.
 */
export function notificationTemplateColumns({
  t,
  localized,
  onEdit,
  onToggle,
  onDelete,
  busy,
  busyId,
  readOnly = false,
}: ColumnOpts): ReactElement[] {
  const openLabel = readOnly
    ? t('admin.notificationTemplates.actions.view')
    : t('admin.notificationTemplates.actions.edit')
  return [
    <Column
      key="operationCodeName"
      field="operationCodeName"
      header={t('admin.notificationTemplates.columns.operationCode')}
      sortable
      body={(row: NotificationTemplate) => row.operationCodeName ?? ''}
    />,
    <Column
      key="notificationTypeName"
      field="notificationTypeName"
      header={t('admin.notificationTemplates.columns.notificationType')}
      style={{ width: '12rem' }}
      sortable
      body={(row: NotificationTemplate) => row.notificationTypeName ?? ''}
    />,
    <Column
      key="subject"
      // Sıralama SUNUCUDA: bu tablo `lazy` çalışıyor ve arka uç beyaz listesi dile duyarlı
      // (`SORT_FIELDS_TR`/`SORT_FIELDS_EN`). `field` bu yüzden korunur — istemciye sortFunction
      // koymak açık sayfayı yeniden sıralar ve sunucu sıralamasını bozar.
      field="subjectTr"
      header={t('admin.notificationTemplates.columns.subject')}
      sortable
      // Ekran uzun süre "Konu (TR)" gösteriyordu: İngilizce çalışan kullanıcı hiç okuyamadığı bir
      // dili görüyordu. Değer artık seçili dilden gelir.
      body={(row: NotificationTemplate) => localized(row.subjectTr, row.subjectEn)}
    />,
    <Column
      key="mailTo"
      field="mailTo"
      header={t('admin.notificationTemplates.columns.mailTo')}
      sortable
      body={(row: NotificationTemplate) => row.mailTo ?? ''}
    />,
    <Column
      key="status"
      header={t('admin.notificationTemplates.columns.status')}
      style={{ width: '8rem' }}
      // Hücre etiket (Badge) basar → `field` yok; sıralama alanı açıkça verilir.
      sortable
      sortField="recordStatus"
      body={(row: NotificationTemplate) => {
        const active = row.recordStatus === 1
        return (
          <Badge tone={active ? 'green' : 'neutral'}>
            {active
              ? t('admin.notificationTemplates.status.active')
              : t('admin.notificationTemplates.status.passive')}
          </Badge>
        )
      }}
    />,
    <Column
      key="actions"
      header={t('admin.notificationTemplates.columns.actions')}
      style={{ width: '6rem' }}
      body={(row: NotificationTemplate) => (
        <div className="admin-row-actions">
          <InputSwitch
            className="admin-switch"
            checked={row.recordStatus === 1}
            disabled={readOnly || busyId === row.id}
            onChange={(e) => onToggle(row, Boolean(e.value))}
            aria-label={row.subjectTr}
            // Salt okunurda anahtar yalnız durumu gösterir; "Aktif/Pasif yap" ipucu yanlış vaat olurdu.
            tooltip={readOnly ? undefined : t('admin.notificationTemplates.actions.toggle')}
            tooltipOptions={{ position: 'bottom' }}
          />
          <Button
            icon={readOnly ? 'pi pi-eye' : 'pi pi-pencil'}
            rounded
            text
            /* Masaüstü karşılığı da aynı sorunu taşıyordu (THY AI review, 26.10.2026): satır
               bazlı tablo düğmesi hangi şablonu açtığını söylemiyordu. Tooltip genel kalır
               (görsel bağlam zaten satırdır), erişilebilir ad konuyu içerir. */
            aria-label={`${row.subjectTr}, ${openLabel}`}
            tooltip={openLabel}
            tooltipOptions={{ position: 'bottom' }}
            disabled={busy}
            onClick={() => onEdit(row)}
          />
          {!readOnly && (
            <Button
              icon="pi pi-trash"
              rounded
              text
              severity="danger"
              aria-label={t('admin.notificationTemplates.actions.delete')}
              tooltip={t('admin.notificationTemplates.actions.delete')}
              tooltipOptions={{ position: 'bottom' }}
              disabled={busyId === row.id}
              onClick={() => onDelete(row)}
            />
          )}
        </div>
      )}
    />,
  ]
}
